import axios from "axios";
import React, { useEffect, useRef, useState, useMemo } from "react";
import toast, { Toaster } from "react-hot-toast";
import { toastOptions } from "../common/toastOptions";
import bgImg from "../../pnai-admin-bg.jpg";
import Logo from "../common/Logo";
import cn from "classnames";
import StatTile from "../common/StatTile";
import ColumnResizer from "../common/ColumnResizer";
import { Reveal } from "../common/Reveal";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import Button from "../common/Button";
import Input from "../common/Input";
import Dropdown from "../Settings/Dropdown";
import EditAgencyModal from "./EditAgencyModal";
import AgencyUsersModal from "./AgencyUsersModal";
import InvestigationAbusePanel from "./InvestigationAbusePanel";
import SurveyResultsPanel from "./SurveyResultsPanel";
import StaffHeader, { STAFF_CONTAINER } from "./StaffHeader";
import ChangeUserEmailModal from "./ChangeUserEmailModal";
import InviteAgencyModal from "./InviteAgencyModal";
import { ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { getEvaluation } from "./evaluation";

type Agency = {
  id: string;
  name: string;
  email: string;
  city: string | null;
  state: string | null;
  max_users: number;
  sworn_officers: number | null;
  trial_seats: number | null;
  trial_expires_at: string | null;
  trial_code: string | null;
  findings_generated_at: string | null;
  findings_slug: string | null;
  findings_type: "trial" | "annual" | null;
  survey_round: number;
  survey_completed: number;
  officer_count: number;
  last_case_at: string | null;
  investigator_seats: { purchased: number; assigned: number; source: string | null } | null;
  archived_at: string | null;
  // One of the staff emails served by /deal-owners; null = unassigned.
  deal_owner: string | null;
  created_at: Date;
  subscription: {
    current_period_end: string;
    status: string;
    created_at: string;
    platform_subscription_id: string | null;
  } | null;
};

// Effective seat count: purchased seats plus unexpired evaluation seats —
// mirrors the agency dashboard's Total Seats math.
const getEffectiveSeats = (agency: Agency) => {
  const evaluation = getEvaluation(agency);
  return agency.max_users + (evaluation?.active ? evaluation.seats : 0);
};

// Derived status used for display, search, and sorting. A real
// subscription always wins; otherwise fall back to the evaluation state.
const getDerivedStatus = (agency: Agency) => {
  if (agency.subscription) return agency.subscription.status;
  const evaluation = getEvaluation(agency);
  if (evaluation) return evaluation.active ? "evaluation" : "evaluation_expired";
  return "";
};

// Internal/test departments left out of the subscription totals.
const TOTALS_EXCLUDED_AGENCIES = new Set(["police narratives ai pd", "12-analytics llc"]);

// "michael@policenarratives.ai" → "Michael".
const ownerLabel = (ownerEmail: string | null) => {
  if (!ownerEmail) return "Unassigned";
  const local = ownerEmail.split("@")[0];
  return local.charAt(0).toUpperCase() + local.slice(1);
};

type SortColumn =
  | "name"
  | "email"
  | "location"
  | "max_users"
  | "sworn_officers"
  | "subscription_start"
  | "subscription_end"
  | "status"
  | "deal_owner";
type SortDirection = "asc" | "desc";

// Column headers are hidden on phones, so the card list gets a sort control.
const SORT_OPTIONS: { key: SortColumn; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "location", label: "Location" },
  { key: "max_users", label: "Seats" },
  { key: "sworn_officers", label: "Sworn" },
  { key: "subscription_start", label: "Start date" },
  { key: "subscription_end", label: "End date" },
  { key: "status", label: "Status" },
  { key: "deal_owner", label: "Deal owner" },
];

// Filter groups above the table. Status buckets the derived status; the
// rest key off list fields the server now sends.
const STATUS_FILTERS = [
  { key: "all", label: "All statuses" },
  { key: "active", label: "Active" },
  { key: "trialing", label: "Trialing" },
  { key: "evaluation", label: "Evaluation" },
  { key: "evaluation_expired", label: "Evaluation expired" },
  { key: "lapsed", label: "Canceled / past due" },
  { key: "none", label: "No subscription" },
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number]["key"];
const statusBucket = (agency: Agency): Exclude<StatusFilter, "all"> => {
  const s = getDerivedStatus(agency);
  if (s === "active" || s === "trialing" || s === "evaluation" || s === "evaluation_expired") return s;
  if (!s) return "none";
  return "lapsed";
};
type InvestigationsFilter = "all" | "seats" | "assigned" | "none";
type ActivityFilter = "all" | "30d" | "90d" | "stale" | "never";
type FindingsFilter = "all" | "yes" | "no";

function AgencyManagement() {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [selectedAgencyForUsers, setSelectedAgencyForUsers] =
    useState<Agency | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [investigationsFilter, setInvestigationsFilter] = useState<InvestigationsFilter>("all");
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");
  const [findingsFilter, setFindingsFilter] = useState<FindingsFilter>("all");
  // "all", "none" (unassigned), or an owner email.
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [dealOwners, setDealOwners] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  // Column widths the staff drag to, remembered per browser.
  const [storedWidths, setStoredWidths] = useLocalStorageState<Record<string, number | undefined>>(
    "pnai-staff-agencies-col-widths",
    {}
  );
  const editModalRef = useRef<HTMLDialogElement>(null);
  const usersModalRef = useRef<HTMLDialogElement>(null);
  const changeEmailModalRef = useRef<HTMLDialogElement>(null);
  const inviteAgencyModalRef = useRef<HTMLDialogElement>(null);
  const adminActionsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if user is already authenticated
    const authStatus = localStorage.getItem("agency-management-auth");
    const storedEmail = localStorage.getItem("agency-management-email");
    if (authStatus === "true" && storedEmail) {
      setIsAuthenticated(true);
      setEmail(storedEmail);
      fetchAgencies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    if (!isAuthenticated || !email) return;
    axios
      .get<{ owners: string[] }>("/api/agency-management/deal-owners", { headers: { "x-admin-email": email } })
      .then((r) => setDealOwners(r.data.owners))
      .catch((err) => console.error("Error fetching deal owners:", err));
  }, [isAuthenticated, email]);

  // Optimistic: the row updates immediately and reverts if the save fails.
  const setDealOwner = async (agency: Agency, owner: string | null) => {
    const previous = agency.deal_owner;
    setAgencies((list) => list.map((a) => (a.id === agency.id ? { ...a, deal_owner: owner } : a)));
    try {
      await axios.patch(`/api/agency-management/agencies/${agency.id}/deal-owner`, { adminEmail: email, dealOwner: owner });
    } catch (err: any) {
      setAgencies((list) => list.map((a) => (a.id === agency.id ? { ...a, deal_owner: previous } : a)));
      toast.error(err?.response?.data?.error || "Couldn't update the deal owner");
    }
  };

  const ownerOptions = useMemo(
    () => [{ value: "", label: "Unassigned" }, ...dealOwners.map((o) => ({ value: o, label: ownerLabel(o) }))],
    [dealOwners]
  );

  const renderOwnerDropdown = (agency: Agency, direction: "top" | "bottom" = "bottom") => (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown
        size="xs"
        dense
        direction={direction}
        menuAlign="start"
        options={ownerOptions}
        value={agency.deal_owner ?? ""}
        onChange={(v) => void setDealOwner(agency, v || null)}
      />
    </div>
  );

  const fetchAgencies = async () => {
    try {
      const response = await axios.get("/api/agency-management/agencies");
      setAgencies(response.data);
    } catch (error) {
      console.error("Error fetching agencies:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post("/api/agency-management/verify-password", {
        email,
        password,
      });

      if (response.data.success) {
        localStorage.setItem("agency-management-auth", "true");
        localStorage.setItem("agency-management-email", email);
        setIsAuthenticated(true);
        // Fetch agencies after successful authentication
        await fetchAgencies();
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || "Invalid email or password");
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("agency-management-auth");
    localStorage.removeItem("agency-management-email");
    setIsAuthenticated(false);
    setEmail("");
    setPassword("");
  };

  const handleEdit = (agency: Agency, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAgency(agency);
    editModalRef.current?.showModal();
  };

  const handleRowClick = (agency: Agency) => {
    setSelectedAgencyForUsers(agency);
    usersModalRef.current?.showModal();
  };

  const handleSaveAgency = async (
    agencyId: string,
    maxUsers: number,
    subscriptionEnd: string | null,
    status: string | null,
    originalMaxUsers: number,
    originalSubscriptionEnd: string | null,
    originalStatus: string | null
  ) => {
    try {
      // Track if any changes were made
      let changesMade = false;

      // Update max users only if changed
      if (maxUsers !== originalMaxUsers) {
        await axios.patch(`/api/agency-management/agencies/${agencyId}/max-users`, {
          max_users: maxUsers,
          adminEmail: email,
        });
        changesMade = true;
      }

      // Check if subscription changed
      const subscriptionEndChanged = subscriptionEnd !== originalSubscriptionEnd;
      const statusChanged = status !== originalStatus;

      // Update subscription only if something changed
      if (subscriptionEndChanged || statusChanged) {
        const updateData: any = { adminEmail: email };
        if (subscriptionEndChanged) updateData.current_period_end = subscriptionEnd;
        if (statusChanged) updateData.status = status;

        await axios.patch(`/api/agency-management/agencies/${agencyId}/subscription`, updateData);
        changesMade = true;
      }

      // Refresh the agencies list only if changes were made
      if (changesMade) {
        await fetchAgencies();
      }
    } catch (error) {
      console.error("Error updating agency:", error);
      throw error;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatStatus = (status: string) => {
    switch (status) {
      case "trialing":
        return "Trialing";
      case "active":
        return "Active";
      case "incomplete_expired":
        return "Expired";
      default:
        return status;
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const stateOptions = useMemo(
    () => Array.from(new Set(agencies.map((a) => (a.state || "").trim().toUpperCase()).filter(Boolean))).sort(),
    [agencies]
  );
  const filtersActive = statusFilter !== "all" || stateFilter !== "all" || investigationsFilter !== "all" || activityFilter !== "all" || findingsFilter !== "all" || ownerFilter !== "all";
  const clearFilters = () => {
    setStatusFilter("all");
    setStateFilter("all");
    setInvestigationsFilter("all");
    setActivityFilter("all");
    setFindingsFilter("all");
    setOwnerFilter("all");
  };

  // Filter and sort agencies based on search query and sort settings
  const sortedAgencies = useMemo(() => {
    const now = Date.now();
    const dayMs = 86_400_000;
    const filtered = agencies.filter((agency) => {
      if (statusFilter !== "all" && statusBucket(agency) !== statusFilter) return false;
      if (stateFilter !== "all" && (agency.state || "").trim().toUpperCase() !== stateFilter) return false;
      if (investigationsFilter !== "all") {
        const seats = agency.investigator_seats;
        if (investigationsFilter === "seats" && !(seats && seats.purchased > 0)) return false;
        if (investigationsFilter === "assigned" && !(seats && seats.assigned > 0)) return false;
        if (investigationsFilter === "none" && seats && seats.purchased > 0) return false;
      }
      if (activityFilter !== "all") {
        const last = agency.last_case_at ? new Date(agency.last_case_at).getTime() : null;
        const age = last == null ? Infinity : (now - last) / dayMs;
        if (activityFilter === "30d" && age > 30) return false;
        if (activityFilter === "90d" && age > 90) return false;
        if (activityFilter === "stale" && !(last != null && age > 90)) return false;
        if (activityFilter === "never" && last != null) return false;
      }
      if (findingsFilter === "yes" && !agency.findings_generated_at) return false;
      if (findingsFilter === "no" && agency.findings_generated_at) return false;
      if (ownerFilter === "none" && agency.deal_owner) return false;
      if (ownerFilter !== "all" && ownerFilter !== "none" && agency.deal_owner !== ownerFilter) return false;

      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase();
      const name = agency.name.toLowerCase();
      const email = agency.email.toLowerCase();
      const location = agency.city && agency.state
        ? `${agency.city}, ${agency.state}`.toLowerCase()
        : (agency.city || agency.state || "").toLowerCase();
      const status = getDerivedStatus(agency).toLowerCase();
      const owner = agency.deal_owner ? ownerLabel(agency.deal_owner).toLowerCase() : "";

      return (
        name.includes(query) ||
        email.includes(query) ||
        location.includes(query) ||
        status.includes(query) ||
        (!!owner && owner.includes(query))
      );
    });

    // Then, sort the filtered results
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "email":
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case "location":
          aValue = a.city && a.state
            ? `${a.city}, ${a.state}`.toLowerCase()
            : (a.city || a.state || "").toLowerCase();
          bValue = b.city && b.state
            ? `${b.city}, ${b.state}`.toLowerCase()
            : (b.city || b.state || "").toLowerCase();
          break;
        case "max_users":
          aValue = getEffectiveSeats(a);
          bValue = getEffectiveSeats(b);
          break;
        case "sworn_officers":
          aValue = a.sworn_officers ?? -1;
          bValue = b.sworn_officers ?? -1;
          break;
        case "subscription_start":
          aValue = a.subscription?.created_at
            ? new Date(a.subscription.created_at).getTime()
            : 0;
          bValue = b.subscription?.created_at
            ? new Date(b.subscription.created_at).getTime()
            : 0;
          break;
        case "subscription_end":
          aValue = a.subscription?.current_period_end
            ? new Date(a.subscription.current_period_end).getTime()
            : getEvaluation(a)?.expiresAt.getTime() || 0;
          bValue = b.subscription?.current_period_end
            ? new Date(b.subscription.current_period_end).getTime()
            : getEvaluation(b)?.expiresAt.getTime() || 0;
          break;
        case "status":
          aValue = getDerivedStatus(a);
          bValue = getDerivedStatus(b);
          break;
        case "deal_owner":
          // Unassigned sorts last ascending.
          aValue = a.deal_owner ? ownerLabel(a.deal_owner).toLowerCase() : "\uffff";
          bValue = b.deal_owner ? ownerLabel(b.deal_owner).toLowerCase() : "\uffff";
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [agencies, sortColumn, sortDirection, searchQuery, statusFilter, stateFilter, investigationsFilter, activityFilter, findingsFilter, ownerFilter]);

  // Paying customers: agencies whose subscription status is "active",
  // counted by purchased seats (not sworn officers). Archived agencies still
  // count while their subscription is active.
  const subscriptionTotals = useMemo(() => {
    const paying = agencies.filter(
      (a) =>
        a.subscription?.status === "active" &&
        !TOTALS_EXCLUDED_AGENCIES.has(a.name.trim().toLowerCase())
    );
    return {
      agencies: paying.length,
      seats: paying.reduce((sum, a) => sum + (a.max_users || 0), 0),
    };
  }, [agencies]);

  const visibleAgencies = useMemo(() => sortedAgencies.filter((a) => !a.archived_at), [sortedAgencies]);
  const archivedAgencies = useMemo(() => sortedAgencies.filter((a) => a.archived_at), [sortedAgencies]);
  const archivedTotal = useMemo(() => agencies.filter((a) => a.archived_at).length, [agencies]);

  const renderStatusBadge = (agency: Agency) =>
    agency.subscription ? (
      <span
        className={`px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
          agency.subscription.status === "active"
            ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400"
            : agency.subscription.status === "trialing"
            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300"
            : "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400"
        }`}
      >
        {formatStatus(agency.subscription.status)}
      </span>
    ) : getEvaluation(agency) ? (
      <span
        className={`px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
          getEvaluation(agency)?.active
            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300"
            : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400"
        }`}
        title={
          agency.trial_code
            ? `Evaluation code ${agency.trial_code}`
            : undefined
        }
      >
        {getEvaluation(agency)?.active
          ? "Evaluation"
          : "Eval Ended"}
      </span>
    ) : (
      <span className="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-base-200 text-gray-800 dark:text-base-content/80">
        None
      </span>
    );

  const renderLocation = (agency: Agency) =>
    agency.city && agency.state
      ? `${agency.city}, ${agency.state}`
      : agency.city || agency.state || "N/A";

  // Phone layout: one card per agency in place of the table row.
  const renderAgencyCard = (agency: Agency) => {
    const evaluation = getEvaluation(agency);
    const endDate =
      agency.subscription?.current_period_end ||
      (evaluation ? agency.trial_expires_at : null);
    return (
      <li
        key={agency.id}
        className="p-4 active:bg-base-200 cursor-pointer"
        onClick={() => handleRowClick(agency)}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-base-content truncate">
              {agency.name}
            </div>
            <div className="text-xs text-base-content/60 truncate">
              {agency.email}
            </div>
            <div className="text-xs text-base-content/60 truncate">
              {renderLocation(agency)}
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {renderStatusBadge(agency)}
            <Button
              onClick={(e) => handleEdit(agency, e)}
              variant="secondary"
              size="sm"
              className="text-xs px-3 py-1"
            >
              Edit
            </Button>
          </div>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs m-0">
          <div>
            <dt className="uppercase text-[10px] font-medium text-base-content/50">Seats</dt>
            <dd className="text-base-content m-0">
              {getEffectiveSeats(agency)}
              {evaluation?.active && (
                <span className="ml-1 text-blue-600 dark:text-blue-300">
                  incl. {evaluation.seats} eval
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="uppercase text-[10px] font-medium text-base-content/50">Sworn</dt>
            <dd className="text-base-content m-0">{agency.sworn_officers ?? "—"}</dd>
          </div>
          <div>
            <dt className="uppercase text-[10px] font-medium text-base-content/50">Start</dt>
            <dd className="text-base-content m-0">
              {formatDate(agency.subscription?.created_at || null)}
            </dd>
          </div>
          <div>
            <dt className="uppercase text-[10px] font-medium text-base-content/50">
              {agency.archived_at ? "Archived" : "End"}
            </dt>
            <dd className="text-base-content m-0">
              {formatDate(agency.archived_at ?? endDate)}
            </dd>
          </div>
          <div className="col-span-2">
            <dt className="uppercase text-[10px] font-medium text-base-content/50">Deal owner</dt>
            <dd className="m-0">{renderOwnerDropdown(agency)}</dd>
          </div>
          {(agency.findings_generated_at && agency.findings_slug) || agency.survey_completed > 0 ? (
            <div className="col-span-2" onClick={(e) => e.stopPropagation()}>
              <dt className="uppercase text-[10px] font-medium text-base-content/50">Trial findings</dt>
              <dd className="text-base-content m-0">
                {agency.findings_generated_at && agency.findings_slug ? (
                  <a
                    href={`${
                      agency.findings_type === "annual"
                        ? "/department-review"
                        : "/trial-findings"
                    }/${agency.findings_slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 hover:underline"
                  >
                    {new Date(agency.findings_generated_at).toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric" }
                    )}{" "}
                    ↗
                  </a>
                ) : (
                  <span className="text-base-content/40">—</span>
                )}
                {agency.survey_completed > 0 && (
                  <span className="ml-2 text-base-content/60">
                    Survey: {agency.survey_completed}
                  </span>
                )}
              </dd>
            </div>
          ) : null}
        </dl>
      </li>
    );
  };

  // One entry per table column: header, sort key, default and minimum width,
  // and how to draw the cell. Widths are px; the table stretches them to fill.
  type AgencyColumn = {
    key: string;
    label: string;
    sort?: SortColumn;
    width: number;
    min: number;
    cell: (agency: Agency, rowIndex: number) => React.ReactNode;
    tdClassName?: string;
    stopClick?: boolean;
  };
  const findingsHref = (agency: Agency) =>
    `${agency.findings_type === "annual" ? "/department-review" : "/trial-findings"}/${agency.findings_slug}`;
  const AGENCY_COLUMNS: AgencyColumn[] = [
    { key: "name", label: "Name", sort: "name", width: 200, min: 120, tdClassName: "truncate font-medium text-base-content", cell: (a) => a.name },
    { key: "email", label: "Email", sort: "email", width: 200, min: 120, tdClassName: "truncate text-base-content/60", cell: (a) => a.email },
    { key: "location", label: "Location", sort: "location", width: 130, min: 90, tdClassName: "truncate text-base-content/60", cell: (a) => renderLocation(a) },
    {
      key: "seats",
      label: "Seats",
      sort: "max_users",
      width: 80,
      min: 60,
      tdClassName: "text-base-content/60",
      cell: (a) => (
        <>
          {getEffectiveSeats(a)}
          {getEvaluation(a)?.active && <span className="block text-xs text-blue-600 dark:text-blue-300">incl. {getEvaluation(a)?.seats} eval</span>}
        </>
      ),
    },
    { key: "sworn", label: "Sworn", sort: "sworn_officers", width: 70, min: 56, tdClassName: "text-base-content/60", cell: (a) => a.sworn_officers ?? "—" },
    { key: "start", label: "Sub start", sort: "subscription_start", width: 100, min: 80, tdClassName: "whitespace-nowrap text-base-content/60", cell: (a) => formatDate(a.subscription?.created_at || null) },
    {
      key: "end",
      label: "Sub end",
      sort: "subscription_end",
      width: 100,
      min: 80,
      tdClassName: "whitespace-nowrap text-base-content/60",
      cell: (a) => formatDate(a.subscription?.current_period_end || (getEvaluation(a) ? a.trial_expires_at : null)),
    },
    { key: "status", label: "Status", sort: "status", width: 110, min: 80, cell: (a) => renderStatusBadge(a) },
    {
      key: "owner",
      label: "Owner",
      sort: "deal_owner",
      width: 120,
      min: 90,
      tdClassName: "!px-2",
      // The table scrolls horizontally, which clips a downward menu on the
      // last rows; open those up.
      cell: (a, i) => renderOwnerDropdown(a, visibleAgencies.length > 4 && i >= visibleAgencies.length - 3 ? "top" : "bottom"),
    },
    {
      key: "findings",
      label: "Trial findings",
      width: 120,
      min: 90,
      tdClassName: "text-xs whitespace-nowrap",
      stopClick: true,
      cell: (a) => (
        <>
          {a.findings_generated_at && a.findings_slug ? (
            <a
              href={findingsHref(a)}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 hover:underline"
              title={`Generated ${new Date(a.findings_generated_at).toLocaleString()}`}
            >
              {new Date(a.findings_generated_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} ↗
            </a>
          ) : (
            <span className="text-gray-300 dark:text-base-content/30">—</span>
          )}
          {a.survey_completed > 0 && <div className="text-[11px] text-base-content/60 mt-0.5">Survey: {a.survey_completed}</div>}
        </>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      width: 80,
      min: 70,
      cell: (a) => (
        <Button onClick={(e) => handleEdit(a, e)} variant="primary" size="sm" className="text-xs px-3 py-1">
          Edit
        </Button>
      ),
    },
  ];
  const widthOf = (c: AgencyColumn) => Math.max(c.min, storedWidths[c.key] ?? c.width);
  const tableMinWidth = AGENCY_COLUMNS.reduce((n, c) => n + widthOf(c), 0);

  if (isAuthenticated) {
    const stopRow = (e: React.MouseEvent) => e.stopPropagation();
    return (
      <div className="min-h-screen bg-base-200">
        <StaffHeader onLogout={handleLogout} />

        <main className={`${STAFF_CONTAINER} py-6 space-y-6`}>
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="m-0 text-3xl font-semibold text-base-content">Agencies</h1>
              <p className="m-0 mt-1 text-sm text-base-content/60">
                Showing {visibleAgencies.length} of {agencies.length - archivedTotal} agencies
                {archivedTotal > 0 && ` (${archivedTotal} archived)`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => inviteAgencyModalRef.current?.showModal()} variant="primary" size="sm" className="text-sm px-4">
                Invite agency
              </Button>
                  <div
                    className="dropdown dropdown-end"
                    ref={adminActionsDropdownRef}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      tabIndex={0}
                      className="animate-none flex items-center text-sm px-4 max-w-fit"
                      onClick={() => {
                        const content =
                          adminActionsDropdownRef.current?.querySelector(
                            ".dropdown-content"
                          ) as HTMLElement;
                        if (content?.matches(":focus-within")) {
                          content.blur();
                        }
                      }}
                    >
                      <span className="truncate flex-1 text-left">
                        Admin Actions
                      </span>
                      <ChevronDownIcon className="size-[18px] flex-shrink-0" />
                    </Button>
                    <ul
                      tabIndex={0}
                      className="dropdown-content menu bg-base-100 rounded-lg z-[1] p-2 shadow m-0 mt-1 overflow-y-auto w-56 text-sm"
                    >
                      <li className="m-0 p-0 hover:cursor-pointer w-full">
                        <p
                          onClick={(e) => {
                            e.preventDefault();
                            (
                              adminActionsDropdownRef.current?.querySelector(
                                ".dropdown-content"
                              ) as HTMLElement
                            )?.blur();
                            changeEmailModalRef.current?.showModal();
                          }}
                          className="hover:bg-gray-100 dark:hover:bg-base-200 m-0 font-medium"
                        >
                          Change User Email
                        </p>
                      </li>
                    </ul>
                  </div>
            </div>
          </Reveal>

          <Reveal index={1} className="grid grid-cols-2 gap-3 md:w-1/2">
            <StatTile label="Active agencies" value={subscriptionTotals.agencies.toLocaleString()} hint="excludes internal test departments" />
            <StatTile label="Subscribed seats" value={subscriptionTotals.seats.toLocaleString()} hint="across active agencies" />
          </Reveal>

          <Reveal index={2} className="rounded-xl border border-base-300 bg-base-100">
            <div className="flex flex-wrap items-center gap-2 p-3 border-b border-base-300">
              <div className="relative w-full md:w-72">
                <MagnifyingGlassIcon className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/40" />
                <input
                  type="text"
                  className="input input-bordered input-sm bg-base-100 pl-8 w-full"
                  placeholder="Search name, email, location, status"
                  aria-label="Search agencies"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <Dropdown dense size="sm" menuAlign="start" value={statusFilter} onChange={(v) => setStatusFilter(v as StatusFilter)} options={STATUS_FILTERS.map((o) => ({ value: o.key, label: o.label }))} />
              <Dropdown
                dense
                size="sm"
                menuAlign="start"
                value={stateFilter}
                onChange={setStateFilter}
                options={[{ value: "all", label: "All states" }, ...stateOptions.map((o) => ({ value: o, label: o }))]}
              />
              <Dropdown
                dense
                size="sm"
                menuAlign="start"
                value={investigationsFilter}
                onChange={(v) => setInvestigationsFilter(v as InvestigationsFilter)}
                options={[
                  { value: "all", label: "Investigations: any" },
                  { value: "seats", label: "Has investigator seats" },
                  { value: "assigned", label: "Has investigators assigned" },
                  { value: "none", label: "No Investigations add-on" },
                ]}
              />
              <Dropdown
                dense
                size="sm"
                menuAlign="start"
                value={activityFilter}
                onChange={(v) => setActivityFilter(v as ActivityFilter)}
                options={[
                  { value: "all", label: "Activity: any" },
                  { value: "30d", label: "Case in last 30 days" },
                  { value: "90d", label: "Case in last 90 days" },
                  { value: "stale", label: "No case in 90+ days" },
                  { value: "never", label: "Never wrote a case" },
                ]}
              />
              <Dropdown
                dense
                size="sm"
                menuAlign="start"
                value={findingsFilter}
                onChange={(v) => setFindingsFilter(v as FindingsFilter)}
                options={[
                  { value: "all", label: "Findings: any" },
                  { value: "yes", label: "Findings report generated" },
                  { value: "no", label: "No findings report" },
                ]}
              />
              <Dropdown
                dense
                size="sm"
                menuAlign="start"
                value={ownerFilter}
                onChange={setOwnerFilter}
                options={[
                  { value: "all", label: "Deal owner: any" },
                  { value: "none", label: "Unassigned" },
                  ...dealOwners.map((o) => ({ value: o, label: ownerLabel(o) })),
                ]}
              />
              {filtersActive && (
                <button type="button" onClick={clearFilters} className="text-sm text-blue-600 dark:text-blue-300 hover:underline">
                  Clear filters
                </button>
              )}
              <div className="md:hidden flex items-center gap-1">
                <Dropdown
                  dense
                  size="sm"
                  menuAlign="start"
                  value={sortColumn}
                  onChange={(v) => {
                    setSortColumn(v as SortColumn);
                    setSortDirection("asc");
                  }}
                  options={SORT_OPTIONS.map((o) => ({ value: o.key, label: `Sort: ${o.label}` }))}
                />
                <button
                  type="button"
                  onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
                  className="btn btn-sm btn-ghost px-2"
                  aria-label={sortDirection === "asc" ? "Sort ascending" : "Sort descending"}
                >
                  {sortDirection === "asc" ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                </button>
              </div>
              <span className="ml-auto text-xs text-base-content/50">Drag a column edge to resize, double-click to reset</span>
            </div>

            <ul className="md:hidden m-0 p-0 list-none divide-y divide-base-300">
              {visibleAgencies.length === 0 ? (
                <li className="p-4 text-sm text-base-content/60">No agencies match the current search or filters.</li>
              ) : (
                visibleAgencies.map(renderAgencyCard)
              )}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <table className="table table-sm table-fixed" style={{ width: "100%", minWidth: tableMinWidth }}>
                <colgroup>
                  {AGENCY_COLUMNS.map((c) => (
                    <col key={c.key} style={{ width: widthOf(c) }} />
                  ))}
                </colgroup>
                <thead className="bg-base-200">
                  <tr>
                    {AGENCY_COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        scope="col"
                        aria-sort={c.sort && sortColumn === c.sort ? (sortDirection === "asc" ? "ascending" : "descending") : undefined}
                        className="relative px-3 py-2 text-left text-xs font-medium text-base-content/60 whitespace-nowrap select-none"
                      >
                        {c.sort ? (
                          <button type="button" className="inline-flex items-center gap-1 hover:text-base-content max-w-full" onClick={() => handleSort(c.sort!)}>
                            <span className="truncate">{c.label}</span>
                            {sortColumn === c.sort && (sortDirection === "asc" ? <ChevronUpIcon className="size-3 shrink-0" /> : <ChevronDownIcon className="size-3 shrink-0" />)}
                          </button>
                        ) : (
                          <span className="truncate">{c.label}</span>
                        )}
                        <ColumnResizer
                          width={widthOf(c)}
                          min={c.min}
                          onResize={(w) => setStoredWidths((prev) => ({ ...prev, [c.key]: w }))}
                          onReset={() => setStoredWidths((prev) => ({ ...prev, [c.key]: undefined }))}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleAgencies.map((agency, rowIndex) => (
                    <tr key={agency.id} className="hover cursor-pointer border-t border-base-200" onClick={() => handleRowClick(agency)}>
                      {AGENCY_COLUMNS.map((c) => (
                        <td key={c.key} className={cn("px-3 py-2 text-sm", c.tdClassName)} onClick={c.stopClick ? stopRow : undefined}>
                          {c.cell(agency, rowIndex)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {visibleAgencies.length === 0 && (
                    <tr>
                      <td colSpan={AGENCY_COLUMNS.length} className="px-3 py-8 text-center text-sm text-base-content/50">
                        No agencies match the current search or filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Reveal>

          <SurveyResultsPanel adminEmail={email} />
          <InvestigationAbusePanel adminEmail={email} />

          {archivedTotal > 0 && (
            <div className="rounded-xl border border-base-300 bg-base-100 overflow-hidden">
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className="w-full flex items-center justify-between p-4 text-left"
                aria-expanded={showArchived}
              >
                <span>
                  <span className="text-lg font-semibold text-base-content">Archived agencies</span>
                  <span className="ml-2 text-sm text-base-content/60">
                    {archivedAgencies.length === archivedTotal
                      ? archivedTotal
                      : `${archivedAgencies.length} of ${archivedTotal} match`}
                  </span>
                </span>
                {showArchived ? (
                  <ChevronUpIcon className="size-5 text-base-content/60" />
                ) : (
                  <ChevronDownIcon className="size-5 text-base-content/60" />
                )}
              </button>
              {showArchived && (
                archivedAgencies.length === 0 ? (
                  <p className="px-4 pb-4 m-0 text-sm text-base-content/60">No archived agencies match the current search or filters.</p>
                ) : (
                  <>
                  <ul className="md:hidden m-0 p-0 list-none divide-y divide-base-300 border-t border-base-300">
                    {archivedAgencies.map(renderAgencyCard)}
                  </ul>
                  <div className="hidden md:block overflow-x-auto border-t border-base-300">
                  <table className="table table-sm w-full">
                    <thead>
                      <tr><th>Name</th><th>Email</th><th>Location</th><th>Seats</th><th>Status</th><th>Archived</th><th /></tr>
                    </thead>
                    <tbody>
                      {archivedAgencies.map((agency) => (
                        <tr
                          key={agency.id}
                          className="hover:bg-base-200 cursor-pointer"
                          onClick={() => handleRowClick(agency)}
                        >
                          <td className="font-medium text-base-content">{agency.name}</td>
                          <td className="text-base-content/60">{agency.email}</td>
                          <td className="text-base-content/60">{renderLocation(agency)}</td>
                          <td className="text-base-content/60">{getEffectiveSeats(agency)}</td>
                          <td>{renderStatusBadge(agency)}</td>
                          <td className="text-base-content/60 whitespace-nowrap">{formatDate(agency.archived_at)}</td>
                          <td>
                            <Button onClick={(e) => handleEdit(agency, e)} variant="primary" size="sm" className="text-xs px-3 py-1">
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  </>
                )
              )}
            </div>
          )}

        </main>

      {/* Edit Modal */}
      <EditAgencyModal
        modalRef={editModalRef}
        agency={selectedAgency}
        onSave={handleSaveAgency}
        onRefresh={fetchAgencies}
        adminEmail={email}
      />

      {/* Users Modal */}
      <AgencyUsersModal
        modalRef={usersModalRef}
        agencyId={selectedAgencyForUsers?.id || null}
        agencyName={selectedAgencyForUsers?.name || null}
        adminEmail={email}
        onUserChanged={fetchAgencies}
      />

      {/* Change User Email Modal */}
      <ChangeUserEmailModal
        modalRef={changeEmailModalRef}
        adminEmail={email}
      />

      {/* Invite Agency (trial) Modal */}
      <InviteAgencyModal
        modalRef={inviteAgencyModalRef}
        adminEmail={email}
      />

      <Toaster position="bottom-center" toastOptions={toastOptions} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2">
      <div
        className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImg})` }}
      >
        <div className="absolute inset-0 bg-blue-50/70 dark:bg-blue-900/70"></div>
        <div className="w-3/5 z-10">
          <Logo className="w-full h-auto" />
        </div>
      </div>
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
        <div className="bg-base-100 p-8 rounded-xl shadow-md dark:border dark:border-base-300 w-full max-w-sm">
          <h1 className="text-3xl mb-4 font-semibold">Agency Management</h1>
          <p className="text-gray-600 dark:text-base-content/70 mb-6 text-sm">
            Enter your email and password to access the agency management portal
          </p>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <p className="font-semibold pb-1 text-sm">Email</p>
              <Input
                type="email"
                id="email"
                value={email}
                placeholder="Enter your email"
                onChange={(e) => {
                  setError("");
                  setEmail(e.target.value);
                }}
                className={`input w-full ${error && "border-red-500"}`}
              />
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm">Password</p>
              <Input
                type="password"
                id="password"
                value={password}
                placeholder="Enter password"
                onChange={(e) => {
                  setError("");
                  setPassword(e.target.value);
                }}
                className={`input w-full ${error && "border-red-500"}`}
              />
              {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
            </div>
            <div>
              <Button
                className="w-full"
                variant="primary"
                type="submit"
                disabled={loading}
              >
                {loading ? "Verifying..." : "Access Portal"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AgencyManagement;
